import React, { useCallback, useEffect, useState } from 'react';
import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    IconButton,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { baseUrl } from '../../environment/environment';
import { httpFetch } from '../../api/Extensions';

import { FormDialogTitle } from '../../common/components/form/FormPrimitives';

import { ResponsiveDialog } from '../../common/components/mobile/MobilePrimitives';

interface ApiAccessTokenDto {
    id: number;
    name: string;
    tokenPrefix: string;
    expiresAt?: string | null;
    isActive: boolean;
    lastUsedAt?: string | null;
    usageCount: number;
    created?: string;
}

interface ApiAccessTokenCreatedDto extends ApiAccessTokenDto {
    plainToken: string;
}

const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

function buildPublicUrl(plainToken: string): string {
    return `${window.location.origin}/public/stock?apiKey=${encodeURIComponent(plainToken)}`;
}

export default function ApiTokensPage(): React.ReactElement {
    const [tokens, setTokens] = useState<ApiAccessTokenDto[] | null>(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [newName, setNewName] = useState('');
    const [newExpiresAt, setNewExpiresAt] = useState('');
    const [createdToken, setCreatedToken] = useState<ApiAccessTokenCreatedDto | null>(null);
    const [busy, setBusy] = useState(false);

    const refresh = useCallback(async () => {
        const res = await httpFetch(`${baseUrl}/api/ApiAccessToken/all`, { headers: jsonHeaders });
        const data = await res.json();
        setTokens(data);
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const handleCreate = async () => {
        if (!newName.trim()) return;
        setBusy(true);
        try {
            const body: { name: string; expiresAt?: string } = { name: newName.trim() };
            if (newExpiresAt) {
                body.expiresAt = new Date(newExpiresAt).toISOString();
            }
            const res = await httpFetch(`${baseUrl}/api/ApiAccessToken`, {
                method: 'POST',
                headers: jsonHeaders,
                body: JSON.stringify(body),
            });
            const created: ApiAccessTokenCreatedDto = await res.json();
            setCreatedToken(created);
            setCreateOpen(false);
            setNewName('');
            setNewExpiresAt('');
            await refresh();
        } finally {
            setBusy(false);
        }
    };

    const handleToggleActive = async (token: ApiAccessTokenDto) => {
        await httpFetch(`${baseUrl}/api/ApiAccessToken/${token.id}/setActive?isActive=${!token.isActive}`, {
            method: 'POST',
            headers: jsonHeaders,
        });
        await refresh();
    };

    const handleDelete = async (token: ApiAccessTokenDto) => {
        if (!window.confirm(`Obrisati token "${token.name}"?`)) return;
        await httpFetch(`${baseUrl}/api/ApiAccessToken/${token.id}`, { method: 'DELETE', headers: jsonHeaders });
        await refresh();
    };

    const copyToClipboard = (value: string) => {
        navigator.clipboard.writeText(value);
    };

    if (tokens === null) {
        return (
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open>
                <CircularProgress color="inherit" />
            </Backdrop>
        );
    }

    return (
        <Box sx={{ p: 4, textAlign: 'start' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">API tokeni za public listu robe</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
                    Novi token
                </Button>
            </Box>

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Naziv</TableCell>
                            <TableCell>Prefiks</TableCell>
                            <TableCell>Aktivan</TableCell>
                            <TableCell>Ističe</TableCell>
                            <TableCell>Poslednja upotreba</TableCell>
                            <TableCell align="right">Broj poziva</TableCell>
                            <TableCell align="right">Akcije</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tokens.map((t) => (
                            <TableRow key={t.id}>
                                <TableCell>{t.name}</TableCell>
                                <TableCell>
                                    <code>{t.tokenPrefix}…</code>
                                </TableCell>
                                <TableCell>
                                    <Switch checked={t.isActive} onChange={() => handleToggleActive(t)} />
                                </TableCell>
                                <TableCell>{t.expiresAt ? new Date(t.expiresAt).toLocaleString() : '—'}</TableCell>
                                <TableCell>{t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleString() : '—'}</TableCell>
                                <TableCell align="right">{t.usageCount}</TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Obriši">
                                        <IconButton onClick={() => handleDelete(t)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                        {tokens.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    Nema kreiranih tokena.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <ResponsiveDialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
                <FormDialogTitle>Novi API token</FormDialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Naziv (npr. Partner X)"
                        fullWidth
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                    />
                    <TextField
                        margin="dense"
                        label="Datum isteka (opciono)"
                        type="datetime-local"
                        fullWidth
                        value={newExpiresAt}
                        onChange={(e) => setNewExpiresAt(e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)} disabled={busy}>
                        Odustani
                    </Button>
                    <Button onClick={handleCreate} variant="contained" disabled={busy || !newName.trim()}>
                        Kreiraj
                    </Button>
                </DialogActions>
            </ResponsiveDialog>

            <ResponsiveDialog open={!!createdToken} onClose={() => setCreatedToken(null)} fullWidth maxWidth="md">
                <FormDialogTitle>Token kreiran</FormDialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                        Sačuvaj ovaj token i URL odmah — token nećeš moći ponovo da vidiš.
                    </Typography>
                    {createdToken && (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <TextField
                                    fullWidth
                                    label="Token"
                                    value={createdToken.plainToken}
                                    slotProps={{ input: { readOnly: true } }}
                                />
                                <IconButton onClick={() => copyToClipboard(createdToken.plainToken)}>
                                    <ContentCopyIcon />
                                </IconButton>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TextField
                                    fullWidth
                                    label="Public URL"
                                    value={buildPublicUrl(createdToken.plainToken)}
                                    slotProps={{ input: { readOnly: true } }}
                                />
                                <IconButton onClick={() => copyToClipboard(buildPublicUrl(createdToken.plainToken))}>
                                    <ContentCopyIcon />
                                </IconButton>
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreatedToken(null)} variant="contained">
                        U redu
                    </Button>
                </DialogActions>
            </ResponsiveDialog>
        </Box>
    );
}
